"use client";

import { NoSuchToolError } from "ai";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { ChatClientPlugin } from "@/lib/ai/chat-client-plugin";
import { ChatClientPluginProvider } from "@/lib/ai/chat-client-plugin";
import { useVizStream } from "@/lib/ai/visualization/client/hooks/use-viz-stream";
import { createVizToolPartPluginResolver } from "@/lib/ai/visualization/client/hooks/use-viz-tool-part-plugin";
import { useVisualizationUiContext } from "@/lib/ai/visualization/client/ui-context";
import {
  VIZ_TOOL_DISPLAY_TITLE,
  VIZ_TOOL_NAME,
} from "@/lib/ai/visualization/config";

function isUnavailableVizRenderError(error: Error): boolean {
  return NoSuchToolError.isInstance(error) && error.toolName === VIZ_TOOL_NAME;
}

/**
 * visualization 機能を AI chat client plugin 契約へ適合させる。
 *
 * @remarks
 * thread 層へ `useVizStream()` や viz 固有 callback を漏らさないため、
 * request body 拡張・stream 処理・error policy・tool 描画をここで束ねる。
 */
export function useVisualizationChatClientPlugin(): ChatClientPlugin {
  const { isVisualizationEnabled } = useVisualizationUiContext();
  const vizStream = useVizStream();
  const resolveToolPartPlugin = useMemo(
    () =>
      createVizToolPartPluginResolver({
        vizStream,
        vizToolTitle: VIZ_TOOL_DISPLAY_TITLE,
      }),
    [vizStream],
  );

  return useMemo(
    () => ({
      id: "visualization",
      extendRequestBody: (body) => {
        body.visualizationEnabled = isVisualizationEnabled;
      },
      handleDataPart: vizStream.handleDataPart,
      shouldSuppressError: (error) =>
        !isVisualizationEnabled && isUnavailableVizRenderError(error),
      onThreadScopeChange: vizStream.reset,
      getToolPartPlugins: (messages) => [resolveToolPartPlugin(messages)],
    }),
    [isVisualizationEnabled, resolveToolPartPlugin, vizStream],
  );
}

/**
 * 汎用 chat plugin host へ visualization plugin を登録する provider。
 *
 * @remarks
 * 小さい composition 用責務を root 配下の別ファイルへ散らさないため、
 * hook と provider を同居させる。host 側へ visualization 固有 import を
 * 持ち込まない契約もここで保つ。
 */
export function VisualizationChatClientPluginProvider({
  children,
}: {
  children: ReactNode;
}) {
  const visualizationPlugin = useVisualizationChatClientPlugin();
  const plugins = useMemo(() => [visualizationPlugin], [visualizationPlugin]);

  return (
    <ChatClientPluginProvider plugins={plugins}>
      {children}
    </ChatClientPluginProvider>
  );
}
